import { QueryClient } from '@tanstack/react-query';
import { isHttpAbortError, toUserFacingHttpError } from '@utils/httpErrors';

export const appQueryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            /** Recovery coordinator refetches after wake; avoid duplicate storms with reconnect. */
            refetchOnReconnect: false,
            networkMode: 'online',
            staleTime: Infinity,
            retry: (failureCount, error) => {
                if (isHttpAbortError(error)) return false;
                if (error?.response?.status === 401) return false;
                return failureCount < 1;
            },
        },
        mutations: {
            retry: (failureCount, error) => {
                if (isHttpAbortError(error)) return false;
                return failureCount < 1;
            },
        },
    },
});

let queriesPausedForRecovery = false;

/** Cancel in-flight queries/mutations and drop cache (logout, session expired, sign-in mount). */
export function resetAppQueryClientCaches() {
    void appQueryClient.cancelQueries({ predicate: () => true });
    appQueryClient.clear();
}

export function pauseAppQueriesForRecovery() {
    if (queriesPausedForRecovery) return;
    queriesPausedForRecovery = true;
    // Switching networkMode to 'offlineFirst' prevents React Query from
    // automatically firing fetches while the app is recovering (e.g. wake/reconnect).
    appQueryClient.setDefaultOptions({ queries: { networkMode: 'offlineFirst' } });
}

export function resumeAppQueriesAfterRecovery() {
    if (!queriesPausedForRecovery) return;
    queriesPausedForRecovery = false;
    // Restore the normal networkMode so queries can fetch again.
    appQueryClient.setDefaultOptions({ queries: { networkMode: 'online' } });
}

/**
 * Refetch active queries in small batches so wake recovery does not saturate the browser connection pool.
 */
export async function refetchActiveQueriesBatched(concurrency = 3) {
    const active = appQueryClient.getQueryCache().findAll({ type: 'active' });
    const limit = Math.max(1, concurrency);
    for (let i = 0; i < active.length; i += limit) {
        const batch = active.slice(i, i + limit);
        await Promise.allSettled(batch.map((query) => query.fetch()));
    }
}

/** @deprecated Use refetchActiveQueriesBatched */
export async function refetchActiveQueriesAfterHttpRecovery() {
    await refetchActiveQueriesBatched(3);
}

export { toUserFacingHttpError };
